// src/encounter/inserted-templates/inserted-templates-host.tsx
// Mounts the inserted-templates controller INSIDE the encounter view, not in the page: the page keeps no live query (its
// mocked suites run with no QueryClient), so the reads and writes of the added templates start here, and only when the
// page hands the view a config. No config → no hook runs, the children are drawn as they were, and the "/" menu offers
// no template.
import type { ReactNode } from 'react';
import { InsertedTemplatesContext, useInsertedTemplatesContext } from './inserted-templates-context';
import { useInsertedTemplates, type InsertedTemplates, type InsertedTemplatesConfig } from './use-inserted-templates';

function Active({ config, children }: { config: InsertedTemplatesConfig; children: ReactNode }) {
  const value = useInsertedTemplates(config);
  return <InsertedTemplatesContext.Provider value={value}>{children}</InsertedTemplatesContext.Provider>;
}

export function InsertedTemplatesHost({ config, children }: { config: InsertedTemplatesConfig | undefined; children: ReactNode }) {
  return config ? <Active config={config}>{children}</Active> : <>{children}</>;
}

/** What the "/" menu of a note section needs: the templates it may add and the way to add one. Read inside the host. */
export function InsertedTemplatesSlot({
  children,
}: {
  children: (props: { insertableTemplates?: InsertedTemplates['insertable']; onInsertTemplate?: InsertedTemplates['insert'] }) => ReactNode;
}) {
  const inserted = useInsertedTemplatesContext();
  return <>{children(inserted ? { insertableTemplates: inserted.insertable, onInsertTemplate: inserted.insert } : {})}</>;
}
